import React, { useCallback, useMemo, useEffect } from 'react'
import Checkbox from '@groovehq/internal-design-system/lib/components/Checkbox/Checkbox'
import {
  text,
  paragraph,
} from '@groovehq/internal-design-system/lib/styles/elements'
import MailboxSelectionTable from 'subapps/settings/components/MailboxSelectionTable'

export default function ShopifyUpdateStoreConfigure({ store, ...rest }) {
  const { reset, register, getValues, setValue } = rest

  /**
   * mailbox for shopify store settings is in the format {mailboxes.1234: true, mailboxes.999: false}
   * react hook forms is NOT a fan of that as it thinks it's a nested property and can never set the values.
   * after numerous hacks, decided to change the format here to something more usable i.e. mailboxes: {1234: true, 999: false}
   * plays better with TableWithCheckbox and react hook forms
   */
  const saneSettings = useMemo(
    () => {
      const { settings: { settings } = {} } = store || {}

      return Object.entries(settings).reduce((obj, [key, value]) => {
        if (!key.startsWith('mailboxes.')) {
          // eslint-disable-next-line no-param-reassign
          obj[key] = value
          return obj
        }

        if (!obj?.mailboxes) {
          // eslint-disable-next-line no-param-reassign
          obj.mailboxes = {}
        }

        const newKey = key.replace('mailboxes.', '')
        // eslint-disable-next-line no-param-reassign
        obj.mailboxes[newKey] = value

        return obj
      }, {})
    },
    [store]
  )

  useEffect(
    () => {
      reset({
        ...saneSettings,
      })
    },
    [saneSettings, reset]
  )

  const onSelectedRowIdsChange = useCallback(
    selectedRowIds => {
      // calculate a diff of previous selected mailboxes state
      // and new state and only setValue on changed selectedIds
      const valuesStateBefore = { ...(getValues()?.mailboxes || {}) }

      Object.entries(valuesStateBefore).forEach(([key, value]) => {
        const selectedValue = selectedRowIds[key] || false
        if (value !== selectedValue) {
          setValue(`mailboxes.${key}`, selectedValue, { shouldDirty: true })
        }
      })
    },
    [setValue, getValues]
  )

  const defaultSelectedRowIds = useMemo(
    () => {
      return saneSettings?.mailboxes || {}
    },
    [saneSettings]
  )

  return (
    <div className="grui mt-12">
      <div
        css={[text.styles.fontMedium, text.styles.textBlack]}
        className="grui mb-5"
      >
        Shopify field syncing
      </div>
      <Checkbox
        id="shopifyStoreSettingsSync"
        css={text.styles.textDark}
        {...register('sync')}
      >
        Save updates to Shopify customers to Groove contacts
      </Checkbox>
      <div
        css={[
          text.styles.text2Xs,
          text.styles.textMediumDark,
          text.styles.italic,
        ]}
        className="grui ml-12 mt-3"
      >
        Note: This will only sync fields going forwards
      </div>
      <div className="grui mt-10">
        <div css={[text.styles.fontMedium, text.styles.textBlack]}>
          {app.t('Mailboxes')}
        </div>
        <p className="grui mt-3 mb-10" css={paragraph.styles.preflight}>
          Choose which {app.t('mailboxes')} you want to display this store in.
        </p>
        <MailboxSelectionTable
          onSelectedRowIdsChange={onSelectedRowIdsChange}
          defaultSelectedRowIds={defaultSelectedRowIds}
        />
      </div>
    </div>
  )
}
